// client/src/components/paymentItems/PaymentItemForm.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { LocaleContext } from '../../contexts/LocaleContext';
import paymentItemService from '../../services/paymentItemService';
import facultyService from '../../services/facultyService';

const PaymentItemForm = () => {
  const { t, i18n } = useTranslation();
  const { user, isAdmin } = useContext(AuthContext);
  const { direction } = useContext(LocaleContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    item_type: 'faculty',
    faculty_id: ''
  });
  const [faculties, setFaculties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEditMode);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Current language
  const lang = i18n.language;

  // Load faculties
  useEffect(() => {
    const loadFaculties = async () => {
      try {
        const response = await facultyService.getFaculties();
        setFaculties(response.data);
        
        // For faculty users, set the faculty_id automatically
        if (!isAdmin() && user.faculty_id && !isEditMode) {
          setFormData(prev => ({
            ...prev,
            faculty_id: user.faculty_id
          }));
        }
      } catch (err) {
        console.error('Error loading faculties', err);
      }
    };
    
    loadFaculties();
  }, [isAdmin, user.faculty_id, isEditMode]);

  // Load payment item data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      const loadPaymentItem = async () => {
        try {
          setFetchLoading(true);
          const response = await paymentItemService.getPaymentItem(id);
          setFormData(response.data);
        } catch (err) {
          setError(t('errorLoadingPaymentItem'));
          navigate('/payment-items');
        } finally {
          setFetchLoading(false);
        }
      };
      
      loadPaymentItem();
    }
  }, [isEditMode, id, navigate, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Reset faculty_id if type changes to university
    if (name === 'item_type' && value === 'university') {
      setFormData({
        ...formData,
        [name]: value,
        faculty_id: ''
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    // Validate required fields
    if (!formData.name) {
      setError(t('nameRequired'));
      setLoading(false);
      return;
    }
    
    if (formData.item_type === 'faculty' && !formData.faculty_id) {
      setError(t('facultyRequired'));
      setLoading(false);
      return;
    }
    
    try {
      // Prepare submission data
      const submissionData = {
        name: formData.name,
        item_type: formData.item_type,
        faculty_id: formData.item_type === 'faculty' ? formData.faculty_id : null
      };
      
      if (isEditMode) {
        await paymentItemService.updatePaymentItem(id, submissionData);
        setSuccess(t('paymentItemUpdatedSuccess'));
      } else {
        await paymentItemService.createPaymentItem(submissionData);
        setSuccess(t('paymentItemCreatedSuccess'));
        setFormData({
          name: '',
          item_type: 'faculty',
          faculty_id: user.faculty_id || ''
        });
      }
    } catch (err) {
      setError(isEditMode ? t('errorUpdatingPaymentItem') : t('errorCreatingPaymentItem'));
    } finally {
      setLoading(false);
      window.scrollTo(0, 0);
    }
  };

  if (fetchLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {isEditMode ? t('editPaymentItem') : t('addPaymentItem')}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="name"
                name="name"
                label={t('paymentItemName')}
                value={formData.name}
                onChange={handleChange}
              />
            </Grid>
            
            {/* Item Type Selection - only admins can create university-wide items */}
            {isAdmin() && (
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">{t('itemType')}</FormLabel>
                  <RadioGroup
                    row
                    name="item_type"
                    value={formData.item_type}
                    onChange={handleChange}
                  >
                    <FormControlLabel 
                      value="university" 
                      control={<Radio />} 
                      label={t('university')} 
                    />
                    <FormControlLabel 
                      value="faculty" 
                      control={<Radio />} 
                      label={t('faculty')} 
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            )}
            
            {/* Faculty Selection - only if type is faculty */}
            {formData.item_type === 'faculty' && (
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel id="faculty-label">{t('faculty')}</InputLabel>
                  <Select
                    labelId="faculty-label"
                    id="faculty_id"
                    name="faculty_id"
                    value={formData.faculty_id}
                    label={t('faculty')}
                    onChange={handleChange}
                    disabled={!isAdmin() && user.faculty_id}
                  >
                    <MenuItem value="">{t('selectFaculty')}</MenuItem>
                    {faculties.map((faculty) => (
                      <MenuItem key={faculty.id} value={faculty.id}>
                        {faculty[`name_${lang}`]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
          
          <Box sx={{ 
            mt: 3, 
            display: 'flex',
            gap: 2,
            justifyContent: direction === 'ltr' ? 'flex-end' : 'flex-start'
          }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/payment-items')}
            >
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              {loading ? t('saving') : t('save')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default PaymentItemForm;